import * as React from "react"
import { cn } from "@/lib/utils"

"use client"

type CircularProgressSize = "sm" | "md" | "lg"

type CircularProgressProps = React.HTMLAttributes<HTMLDivElement> & {
    size?: CircularProgressSize
    value?: number
    label?: React.ReactNode
}

const CIRCUMFERENCE = 2 * Math.PI * 18

const CircularProgress = React.forwardRef<HTMLDivElement, CircularProgressProps>(
    ({ className, size, value, label, style, ...props }, ref) => {
        const sizeClass =
            size === "sm"
                ? "h-10 w-10"
                : size === "lg"
                    ? "h-16 w-16"
                    : "h-12 w-12"
        const progress = value === undefined ? undefined : Math.min(100, Math.max(0, value))
        const dashOffset =
            progress === undefined ? CIRCUMFERENCE * 0.25 : CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE

        return (
            <div
                ref={ref}
                className={cn("relative inline-flex items-center justify-center", sizeClass, className)}
                style={style}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-busy={progress === undefined}
                {...props}
            >
                <svg
                    viewBox="0 0 48 48"
                    className={cn("absolute inset-0 h-full w-full", progress === undefined && "animate-spin")}
                >
                    <circle
                        cx="24"
                        cy="24"
                        r="18"
                        className="stroke-[var(--border-default)] fill-transparent stroke-[4px]"
                    />
                    <circle
                        cx="24"
                        cy="24"
                        r="18"
                        className="fill-transparent stroke-[var(--interactive-primary)] stroke-[4px] transition-all duration-300 ease-out"
                        style={{
                            strokeDasharray: CIRCUMFERENCE,
                            strokeDashoffset: dashOffset,
                            transform: "rotate(-90deg)",
                            transformOrigin: "50% 50%",
                        }}
                    />
                </svg>
                <div className="relative flex h-full w-full items-center justify-center text-[0.65rem] font-medium leading-none text-[var(--text-primary)]">
                    {label ?? (progress !== undefined ? `${progress}%` : null)}
                </div>
            </div>
        )
    }
)

CircularProgress.displayName = "CircularProgress"

export { CircularProgress }
